import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Facebook, Youtube, Linkedin, Mail, Phone, MapPin, ArrowRight, ArrowUp } from "lucide-react";
import { LOGO } from "@/data/images";
import { SITE } from "@/data/site";
import { imgSrc } from "@/lib/img";
import { useImg } from "@/lib/useImg";
import { useSettings } from "@/context/SettingsContext";

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.58-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const SOCIALS = [
  ["instagram", Instagram],
  ["facebook", Facebook],
  ["youtube", Youtube],
  ["linkedin", Linkedin],
  ["whatsapp", WhatsAppIcon],
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const socialHref = (s, key) => {
  const custom = s.social?.[key];
  if (custom) return custom;
  if (key === "whatsapp") {
    const num = (s.whatsapp || SITE.whatsapp || "").replace(/[^0-9]/g, "");
    return num ? `https://wa.me/${num}` : "#";
  }
  return SITE.social[key] || "#";
};

export const Footer = () => {
  const s = useSettings();
  const img = useImg();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Please enter a valid email");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/newsletter`, { email });
      toast.success(data.message);
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const FADE_MASK =
    "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 32%, rgba(0,0,0,0.5) 62%, rgba(0,0,0,0.22) 84%, rgba(0,0,0,0.08) 100%)";

  return (
    <footer data-testid="site-footer" className="relative bg-forestdeep text-cream overflow-hidden">
      {/* Complete landscape as an integrated background; fades continuously from a bright bottom into the plain green at the top */}
      <div className="absolute inset-0 z-0 pointer-events-none" data-testid="footer-illustration" aria-hidden="true">
        <img
          src={img("footerLandscape")}
          alt=""
          className="w-full h-full object-cover select-none"
          draggable="false"
          style={{ objectPosition: "center 88%", WebkitMaskImage: FADE_MASK, maskImage: FADE_MASK }}
        />
      </div>

      <div className="relative z-10 container-nb pt-12 sm:pt-14 pb-24 sm:pb-32 lg:pb-40 [text-shadow:0_1px_12px_rgba(9,18,13,0.6)]">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-x-8 gap-y-8">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-4">
            <div className="inline-block bg-cream rounded-xl px-3 py-2">
              <img src={imgSrc(s.logo) || LOGO} alt="Nutribest Superfoods" className="h-12 w-auto mix-blend-multiply" />
            </div>
            <p className="mt-5 text-cream/70 text-sm leading-relaxed max-w-xs">
              Earned Trust. Pure Nutrition. We exist to earn trust — not to sell products. Because every family deserves food they never have to question.
            </p>
            <p className="mt-5 font-serif text-goldlight tracking-wide">
              Pure Nutrition. <span className="text-cream/30 mx-1">|</span> Real Superfoods.
            </p>
          </div>

          {/* Explore */}
          <div className="lg:col-span-2 lg:border-l lg:border-cream/10 lg:pl-8">
            <h4 className="eyebrow !text-goldlight mb-5">Explore</h4>
            <ul className="space-y-3 text-sm text-cream/80">
              {[["Who We Are", "/"], ["Our Story", "/our-story"], ["The Nutribest Experience", "/experience"], ["All Products", "/collection"], ["Our Quality Standard", "/quality-standard"], ["From Farm to Family", "/farm-to-family"]].map(([label, to]) => (
                <li key={to}>
                  <Link data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} to={to} className="inline-flex items-center gap-2 hover:text-goldlight transition-colors group">
                    <span className="h-1 w-1 rounded-full bg-gold/60 group-hover:bg-goldlight transition-colors" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer Care */}
          <div className="lg:col-span-2 lg:border-l lg:border-cream/10 lg:pl-8">
            <h4 className="eyebrow !text-goldlight mb-5">Customer Care</h4>
            <ul className="space-y-3 text-sm text-cream/80">
              {[["FAQ", "/faq"], ["Our Promise", "/promise"], ["Sustainability", "/sustainability"], ["Knowledge Centre", "/academy"]].map(([label, to]) => (
                <li key={to}>
                  <Link data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} to={to} className="inline-flex items-center gap-2 hover:text-goldlight transition-colors group">
                    <span className="h-1 w-1 rounded-full bg-gold/60 group-hover:bg-goldlight transition-colors" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Get in touch */}
          <div className="col-span-2 lg:col-span-4 lg:border-l lg:border-cream/10 lg:pl-8">
            <h4 className="eyebrow !text-goldlight mb-5">Get In Touch</h4>
            <ul className="space-y-3 text-sm text-cream/80">
              <li className="flex items-center gap-2.5"><Mail size={15} className="text-gold shrink-0" /> <a data-testid="footer-email" href={`mailto:${s.email}`} className="hover:text-goldlight transition-colors">{s.email}</a></li>
              <li className="flex items-center gap-2.5"><Phone size={15} className="text-gold shrink-0" /> <a data-testid="footer-phone" href={`tel:${s.phone.replace(/\s/g, "")}`} className="hover:text-goldlight transition-colors">{s.phone}</a></li>
              <li className="flex items-start gap-2.5"><MapPin size={15} className="text-gold mt-0.5 shrink-0" /> <span data-testid="footer-address" className="leading-relaxed">{s.address}</span></li>
            </ul>
            <div className="flex flex-wrap gap-2.5 mt-6">
              {SOCIALS.map(([key, Icon]) => (
                <a key={key} href={socialHref(s, key)} target="_blank" rel="noreferrer" data-testid={`footer-social-${key}`} aria-label={key} className="w-9 h-9 rounded-full border border-cream/25 bg-forestdeep/40 backdrop-blur-sm flex items-center justify-center text-cream/80 hover:text-forestdeep hover:bg-goldlight hover:border-goldlight transition-colors">
                  <Icon size={15} className="w-[15px] h-[15px]" />
                </a>
              ))}
            </div>

            {/* Newsletter */}
            <div className="mt-8">
              <p className="font-serif text-lg text-cream">Trust grows naturally.</p>
              <p className="text-cream/80 text-xs mt-1 mb-3">Get nourishing ideas, product updates and thoughtful offers.</p>
              <form data-testid="footer-newsletter-form" onSubmit={subscribe} className="flex max-w-sm">
                <input
                  data-testid="footer-newsletter-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="flex-1 bg-forestdeep/50 backdrop-blur-sm border border-cream/25 rounded-l-full px-5 py-3 text-sm placeholder:text-cream/50 focus:outline-none focus:border-goldlight transition-colors"
                />
                <button data-testid="footer-newsletter-submit-button" disabled={busy} className="bg-gold hover:bg-goldlight text-white rounded-r-full px-5 flex items-center gap-1.5 text-sm font-semibold transition-colors disabled:opacity-60">
                  Subscribe <ArrowRight size={14} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Legal bar — compact, with a readability scrim over the bright lower landscape */}
      <div className="relative z-10 bg-gradient-to-t from-forestdeep via-forestdeep/85 to-transparent [text-shadow:0_1px_10px_rgba(9,18,13,0.6)]">
        <div className="container-nb pt-10 pb-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-cream/75 tracking-wide">
          <p className="text-center sm:text-left">© {new Date().getFullYear()} {SITE.legalName} All rights reserved. <span className="hidden sm:inline text-cream/30 mx-1">|</span> <span className="block sm:inline text-cream/55" data-testid="footer-cin">CIN: U10790OD2026PTC054386</span></p>
          <div className="flex items-center gap-4">
            <p className="flex flex-wrap gap-x-3 gap-y-1 justify-center">
              <Link data-testid="footer-link-privacy" to="/legal/privacy-policy" className="hover:text-goldlight transition-colors">Privacy Policy</Link>
              <span>·</span>
              <Link data-testid="footer-link-terms" to="/legal/terms" className="hover:text-goldlight transition-colors">Terms & Conditions</Link>
              <span>·</span>
              <Link data-testid="footer-link-shipping" to="/legal/shipping-returns" className="hover:text-goldlight transition-colors">Shipping & Returns</Link>
            </p>
            <button
              type="button"
              data-testid="footer-back-to-top"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              aria-label="Back to top"
              className="w-8 h-8 rounded-full border border-cream/25 bg-forestdeep/50 flex items-center justify-center text-cream/70 hover:text-forestdeep hover:bg-goldlight hover:border-goldlight transition-colors shrink-0"
            >
              <ArrowUp size={15} />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
